import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, Component } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { CalendarIcon, MapPin, Clock, User, Camera, Play, Maximize2, X, Plus, Loader2, Trash2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { parseDeliveryDate, formatDateForBackend, formatDeliveryDateDisplay } from '../utils/dateUtils';
import { formatPhoneNumberDisplay } from '../utils/phoneUtils';
import { useAuth } from '@/context/AuthContext';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import AddressConfirmationDialog from '@/components/AddressConfirmationDialog';
import { addressReviewFrom, addressAcceptance } from '@/utils/addressConfirmation';
import AddressIntel from '@/components/AddressIntel';
import { savedAccessAddressKey } from '@/utils/accessCodeContext';
import ArrangementPhotoThumbnail from '@/components/ArrangementPhotoThumbnail';
import { useOrderSizes } from '@/hooks/useOrderSizes';
import { orderSizeOptions } from '@/utils/orderSizeOptions';
import SizeChoicesUnavailable from '@/components/SizeChoicesUnavailable';
import { API } from '@/config/constants';
import { addOrderDeliveryMedia, updateOrderDelivery } from '@/api/deliveries';
import { isDesktopOffline } from '@/api/desktopSync';
import { offlineEditContext } from '@/utils/offlineEditContinuity';
import {
  deleteOrderMedia,
  getOrder,
  getOrderCommitmentStatus,
  getOrderProductResolution,
  getOrderSourceResolution,
  getOrderSubsourceResolution,
  createCachedOrderFieldMutation,
  updateLocalOrder,
  updateOrder,
  uploadOrderMedia,
} from '@/api/orders';
import { getUsers } from '@/api/users';
import { useAuthenticatedMedia } from '@/hooks/useAuthenticatedMedia';
import { MEDIA_AVAILABLE } from '@/api/authenticatedMedia';
import { openVideoSnapshot } from '@/utils/videoSnapshot';
import { offlineMediaLabel } from '@/utils/mediaPresentation';
import { useProductReferenceMedia } from '@/hooks/useProductReferenceMedia';
import { useOrderEditorOperation } from '@/hooks/useOrderEditorOperation';
import { productReferenceMediaLabel } from '@/utils/productReference';
import { Info } from 'lucide-react';
import OrderNetValue, { NET_EXPLANATION } from '@/components/OrderNetValue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useOrderNetAmounts } from '@/hooks/useOrderNetAmounts';

const EMPTY_FORM = {
  recipient_name: '', recipient_street_address: '', recipient_street_address_2: '',
  recipient_city: '', recipient_state: '', recipient_zip_code: '', recipient_phone: '',
  order_number: '', network_source: '', subsource_extracted: '', delivery_date: '', order_accepted_date: '',
  card_message: '', special_instructions: '', product_description: '', product_name: '', product_code: '', size: '',
  delivery_time_requirement: '', total_price: '', internal_order_number: '', status: ''
};

// ── Claude Design palette — Order-edit redesign (v2). Scoped to this modal and
// intentionally distinct from the app's Glass-Greenhouse system (cream ground +
// brand green, flat 7px inputs). Reused across tabs as they're migrated. ──
const CD = {
  panel: "rounded-[10px] border border-black/[0.08] bg-[#fbfaf7] p-5",
  panelTitle: "mb-3.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-black/40",
  label: "mb-1.5 block text-[11.5px] font-medium tracking-[0.01em] text-black/55",
  input: "h-auto min-h-0 rounded-[7px] border-black/[0.14] bg-white px-2.5 py-2 text-[13.5px] leading-normal shadow-none md:text-[13.5px]",
  stat: "min-w-0 break-words flex-1 rounded-[9px] border border-black/[0.08] bg-[#f5f4f1] px-3 py-2.5",
  statK: "text-[10px] font-semibold uppercase tracking-[0.06em] text-black/40",
  statV: "mt-0.5 text-[14.5px] font-semibold text-[#1a1a1a]",
};
const CD_STRIPE = { background: 'repeating-linear-gradient(135deg,#eee,#eee 6px,#e4e2dd 6px,#e4e2dd 12px)' };

const CatalogMatch = ({ resolution }) => {
  if (resolution?.unavailable) return <p role="status" className="mb-3 text-sm text-amber-700">Catalog lookup unavailable. Your entered order fields are unchanged.</p>;
  if (!resolution?.resolved) return null;

  const productName = resolution.product?.product_name;
  const sizeLabel = resolution.size_label || resolution.variant?.size_label;
  const recipe = resolution.variant?.recipe_text;
  const retailPrice = resolution.variant?.retail_price;
  const notes = resolution.product?.notes;
  const hasRetailPrice = typeof retailPrice === 'number' && Number.isFinite(retailPrice);

  return (
    <div
      className="mb-3.5 rounded-[9px] border border-emerald-200 bg-emerald-50/60 px-3.5 py-3"
      data-testid="edit-catalog-match"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-emerald-800">
          Catalog match
        </span>
        <span className="rounded-full bg-white/80 px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-emerald-700">
          Read-only
        </span>
      </div>
      {productName && (
        <div className="mt-1.5 text-[14px] font-semibold text-stone-900" data-testid="edit-catalog-product-name">
          {productName}
        </div>
      )}
      {(sizeLabel || hasRetailPrice) && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-stone-600">
          {sizeLabel && <span data-testid="edit-catalog-size">Size: {sizeLabel}</span>}
          {hasRetailPrice && (
            <span data-testid="edit-catalog-price">Catalog price: ${Number(retailPrice).toFixed(2)}</span>
          )}
        </div>
      )}
      {recipe && (
        <div className="mt-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Recipe</div>
          <div className="mt-0.5 whitespace-pre-wrap text-[12.5px] leading-relaxed text-stone-800" data-testid="edit-catalog-recipe">
            {recipe}
          </div>
        </div>
      )}
      {notes && (
        <div className="mt-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Catalog notes</div>
          <div className="mt-0.5 whitespace-pre-wrap text-[12px] leading-relaxed text-stone-600" data-testid="edit-catalog-notes">
            {notes}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Error Boundary for safe rendering ────────────────────── */
class SafeRender extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('SafeRender caught:', error, info); }
  render() {
    if (this.state.hasError) {
      return <div className="text-sm text-stone-400 italic p-4">Unable to display this section</div>;
    }
    return this.props.children;
  }
}

/* ── helpers ─────────────────────────────────────────────── */
function fmtTime(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

/* ── Media Gallery ───────────────────────────────────────── */
const MediaGallery = ({ order, onMediaAdded, operation }) => {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [armedPath, setArmedPath] = useState(null);   // two-click delete arm
  const [deletingPath, setDeletingPath] = useState(null);
  const [userNames, setUserNames] = useState({});   // user id -> display name
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const galleryMounted = useRef(false);
  useLayoutEffect(() => {
    galleryMounted.current = true;
    return () => { galleryMounted.current = false; };
  }, []);
  const armTimer = useRef(null);
  useEffect(() => () => clearTimeout(armTimer.current), []);

  // Resolve designer-credit ids -> names. /api/users is admin-only, so skip
  // for non-admins (who don't reach this dialog in normal navigation anyway);
  // the credit lines then fall back to a subtle placeholder.
  useEffect(() => {
    if (!isAdmin) return;
    const config = operation.config();
    if (!config) return;
    let cancelled = false;
    (async () => {
      try {
        const users = await operation.read(request => getUsers({ include_disabled: true }, request));
        if (cancelled || !operation.owns()) return;
        const map = {};
        for (const u of users) map[u.id] = u.name || u.username;
        setUserNames(map);
      } catch { /* names are non-critical */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const allMedia = useMemo(() => {
    const delivery = (order?.delivery_confirmation?.media_files || [])
      .filter((medium) => medium.file_type !== 'signature')
      .map((medium) => ({ ...medium, _category: 'delivery' }));
    const general = (order?.media_files || [])
      .map((medium) => ({ ...medium, _category: 'general' }));
    return [...general, ...delivery];
  }, [order?.delivery_confirmation?.media_files, order?.media_files]);
  const mediaRequests = useMemo(() => allMedia.flatMap((medium) => medium.storage_path ? [{
    key: medium.storage_path,
    url: `${API}/delivery-media/${encodeURIComponent(medium.storage_path)}`,
  }] : []), [allMedia]);
  const loadedMedia = useAuthenticatedMedia(mediaRequests);
  const mediaIdentity = item => JSON.stringify([order.id, item._category, item.storage_path]);
  const currentGallery = useRef(null);
  currentGallery.current = { allMedia, loadedMedia, mediaRequests, operation, orderId: order.id };
  const openMedia = (item, result) => {
    const key = mediaIdentity(item);
    const current = () => {
      const latest = currentGallery.current;
      return galleryMounted.current && latest.operation.owns()
        && latest.orderId === order.id && latest.mediaRequests === mediaRequests
        && latest.allMedia.some(value => JSON.stringify([latest.orderId, value._category, value.storage_path]) === key)
        && latest.loadedMedia[item.storage_path] === result && result?.state === MEDIA_AVAILABLE;
    };
    if (!current()) return;
    if (item.file_type !== 'video') { setSelectedPhoto(key); return; }
    const outcome = openVideoSnapshot(result.blob, current);
    if (outcome === 'blocked') toast.error('Pop-up blocked. Allow pop-ups to view the video.');
    if (outcome === 'failed') toast.error('Unable to open video. Please try again.');
  };
  const selectedItem = selectedPhoto && operation.owns()
    ? allMedia.find(item => mediaIdentity(item) === selectedPhoto && item.file_type !== 'video') : null;
  // Retire selection during render, not after obsolete pixels have committed.
  // Removed items must not reopen automatically if the same path returns later.
  if (selectedPhoto && !selectedItem) setSelectedPhoto(null);
  const selectedResult = selectedItem ? loadedMedia[selectedItem.storage_path] : null;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !operation.canAct()) return;
    setUploading(true);
    await operation.run({
      label: 'Uploading photos',
      execute: config => uploadOrderMedia(order.id, files, config),
      accepted: async () => {
        toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} saved`);
        await onMediaAdded?.();
      },
      errorMessage: 'Upload failed',
    });
    if (operation.owns()) {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Two-click-arm delete: first click arms (auto-disarms after 3s), second
  // click commits. Routes to the delivery-media or general-media endpoint by
  // category, then refreshes. Files save on upload, so this is the only way
  // to remove a wrongly-uploaded image.
  const handleDelete = async (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!operation.canAct()) return;
    if (armedPath !== item.storage_path) {
      setArmedPath(item.storage_path);
      clearTimeout(armTimer.current);
      armTimer.current = setTimeout(() => {
        if (operation.owns()) setArmedPath((p) => (p === item.storage_path ? null : p));
      }, 3000);
      return;
    }
    setArmedPath(null);
    setDeletingPath(item.storage_path);
    await operation.run({
      label: 'Deleting photo',
      execute: config => deleteOrderMedia(order.id, item.storage_path, item._category, config),
      accepted: async () => { toast.success('Photo deleted'); await onMediaAdded?.(); },
      errorMessage: 'Delete failed',
    });
    if (operation.owns()) setDeletingPath(null);
  };

  const designerName = order?.designed_by_id ? (userNames[order.designed_by_id] || '…') : null;
  const markedByName = order?.designed_marked_by_id ? (userNames[order.designed_marked_by_id] || '…') : null;
  const showMarkedBy = order?.designed_marked_by_id && order.designed_marked_by_id !== order?.designed_by_id;

  return (
    <div>
      {/* Header: title + design-credit caption (CD Photos-panel header). */}
      <div className="mb-3.5 flex items-baseline justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-black/40">Photos</div>
        {order?.designed_by_id && (
          <span className="text-[11.5px] text-black/50" data-testid="design-attribution">
            Designed by <b className="font-semibold text-[#1a1a1a]" data-testid="designed-by-name">{designerName}</b>
            {showMarkedBy && <> · marked by <b className="font-semibold text-[#1a1a1a]" data-testid="marked-designed-by-name">{markedByName}</b></>}
          </span>
        )}
      </div>

      <div className="grid grid-cols-[repeat(auto-fill,88px)] justify-start gap-2.5" data-testid="media-gallery">
        {/* Arrangement photo — always exactly one; heavier border sets it apart. */}
        {order?.arrangement_photo && (
          <ArrangementPhotoThumbnail
            photo={order.arrangement_photo}
            className="h-[88px] w-[88px] rounded-[9px] border-2 border-black/25"
            testId="proofs-arrangement-thumb"
          />
        )}

        {allMedia.map((item, i) => {
          const isVideo = item.file_type === 'video';
          const isDelivery = item._category === 'delivery';
          const result = loadedMedia[item.storage_path];
          const url = result?.url;
          return (
            <div
              key={item.storage_path || i}
              role="button"
              tabIndex={0}
              onClick={() => openMedia(item, result)}
              className="group relative block h-[88px] w-[88px] cursor-pointer overflow-hidden rounded-[9px] border border-black/[0.14] bg-stone-100"
              data-testid={isDelivery ? 'delivery-media-item' : 'media-item'}
            >
              {isVideo && url ? (
                <div className="w-full h-full flex items-center justify-center bg-stone-900">
                  <Play className="h-10 w-10 text-white/70 group-hover:text-white transition-colors" />
                </div>
              ) : url ? (
                <img src={url} alt={`Media ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-out" loading="lazy" />
              ) : result?.state === 'loading' ? (
                <div className="w-full h-full bg-stone-200 animate-pulse" />
              ) : (
                <div className="w-full h-full flex items-center justify-center p-1 text-center text-[9px] text-stone-500">
                  {offlineMediaLabel(result)}
                </div>
              )}
              {isDelivery && (
                <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-100/80 text-emerald-800 border border-emerald-200/50 backdrop-blur-sm">
                  Delivery
                </span>
              )}
              {url && !isVideo && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                  <Maximize2 className="h-5 w-5 text-white opacity-0 group-hover:opacity-80 transition-opacity drop-shadow" />
                </div>
              )}
              {isAdmin && item.storage_path && (
                <button
                  type="button"
                  onClick={(e) => handleDelete(item, e)}
                  disabled={operation.blocked || deletingPath === item.storage_path}
                  className={`absolute top-2 right-2 z-10 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border backdrop-blur-sm transition-colors ${
                    armedPath === item.storage_path
                      ? 'bg-red-600 text-white border-red-700'
                      : 'bg-white/80 text-stone-600 border-stone-200 hover:bg-red-50 hover:text-red-600'
                  }`}
                  data-testid="delete-media-btn"
                  title={armedPath === item.storage_path ? 'Click again to confirm' : 'Delete'}
                >
                  {deletingPath === item.storage_path
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : armedPath === item.storage_path
                      ? 'Confirm?'
                      : <Trash2 className="h-3 w-3" />}
                </button>
              )}
            </div>
          );
        })}

        {/* + Add — single next cell (opens the file picker). */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={operation.blocked || uploading}
          className="flex h-[88px] w-[88px] items-center justify-center rounded-[9px] border-[1.5px] border-dashed border-black/20 bg-white text-[11px] text-black/40 transition-colors hover:border-black/30 hover:bg-black/[0.02]"
          data-testid="add-media-btn"
        >
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : '+ Add'}
        </button>
      </div>
      <input
        ref={fileInputRef}
        data-testid="editor-photo-input"
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleUpload}
      />

      {/* Image lightbox — same overlay pattern as the arrangement photo; z-[70]
          sits above the edit dialog (z-50). Click backdrop or ✕ to close. */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80"
          onClick={() => setSelectedPhoto(null)}
          data-testid="media-lightbox"
        >
          <button
            type="button"
            className="absolute right-4 top-4 text-white/70 transition-colors hover:text-white"
            onClick={() => setSelectedPhoto(null)}
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
          {selectedResult?.state === MEDIA_AVAILABLE && selectedResult.url ? <img
            src={selectedResult.url}
            alt="Media full size"
            className="max-h-[90vh] max-w-[90vw] rounded object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          /> : <p role="status" className="text-white" onClick={(e) => e.stopPropagation()}>
            {!selectedResult || selectedResult.state === 'loading' ? 'Loading photo…'
              : selectedResult.reason ? offlineMediaLabel(selectedResult) : 'Unable to display photo.'}
          </p>}
        </div>
      )}
    </div>
  );
};

/* ── Delivery Confirmation Panel ─────────────────────────── */
const DeliveryPanel = ({ order, onUpdated, operation, onDirtyChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');
  const dc = order?.delivery_confirmation;
  const hasConfirmation = dc && (dc.received_by || dc.confirmed_by_user_name || dc.time_delivered || dc.gps_location);
  // POD edits are only valid on a delivered order; restricted to admins here
  // (the assigned driver edits from the PWA). Backend enforces both regardless.
  const canEdit = isAdmin && order?.status === 'delivered';

  const photoInputRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [savingText, setSavingText] = useState(false);
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [rb, setRb] = useState('');
  const [rel, setRel] = useState('');
  const [notes, setNotes] = useState('');
  const [deliveredAt, setDeliveredAt] = useState('');
  const [baseline, setBaseline] = useState('');
  const draft = JSON.stringify([rb, rel, notes, deliveredAt]);
  useEffect(() => { onDirtyChange(editing && draft !== baseline); }, [editing, draft, baseline, onDirtyChange]);

  // The signature is stored as a 'signature' media file (same as the public
  // receipt), not on dc.signature_url. Resolve it through the authenticated
  // media endpoint into a displayable blob URL for read-only viewing here.
  const signature = useMemo(
    () => (dc?.media_files || []).find((medium) => medium.file_type === 'signature'),
    [dc?.media_files],
  );
  const signatureRequests = useMemo(() => signature?.storage_path ? [{
    key: 'signature',
    url: `${API}/delivery-media/${encodeURIComponent(signature.storage_path)}`,
  }] : [], [signature?.storage_path]);
  const signatureResult = useAuthenticatedMedia(signatureRequests).signature;
  const sigUrl = signatureResult?.url || null;

  // Format a Date into the value <input type="datetime-local"> expects
  // (local time, no zone suffix): YYYY-MM-DDTHH:MM
  const toLocalInput = (d) => {
    if (!d || isNaN(d)) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const startEdit = () => {
    if (!operation.canAct()) return;
    setRb(dc?.received_by || '');
    setRel(dc?.received_by_relationship || '');
    setNotes(dc?.notes || '');
    // Prefill delivery time: existing time if present, else the order's
    // scheduled delivery date at noon (admin adjusts). Handles back-entries
    // for orders delivered on a prior day.
    let initialTime = '';
    if (dc?.time_delivered) {
      initialTime = toLocalInput(new Date(dc.time_delivered));
    } else if (order?.delivery_date) {
      const d = parseDeliveryDate(order.delivery_date);
      if (d && !isNaN(d)) { d.setHours(12, 0, 0, 0); initialTime = toLocalInput(d); }
    }
    setDeliveredAt(initialTime);
    setBaseline(JSON.stringify([dc?.received_by || '', dc?.received_by_relationship || '', dc?.notes || '', initialTime]));
    setEditing(true);
  };

  // The backend blocks POD edits on a closed run; surface that as an actionable hint.
  const podError = (err) => {
    const detail = err.response?.data?.detail || '';
    if (/run is closed/i.test(detail)) return 'This delivery’s run is closed. Reopen it from the Deliver tab to edit.';
    return detail || 'Update failed';
  };

  const saveText = async () => {
    if (!operation.canAct()) return;
    if (!rb.trim()) { toast.error('Received by cannot be empty'); return; }

    // Only send fields that actually changed. Photos save on upload (they don't
    // go through this Save), so an accidental Save with no text edits should be
    // a benign no-op — not a scary "No changes detected" error.
    const payload = {};
    if (rb.trim() !== (dc?.received_by || '')) payload.received_by = rb.trim();
    if (rel.trim() !== (dc?.received_by_relationship || '')) payload.received_by_relationship = rel.trim();
    if (notes.trim() !== (dc?.notes || '')) payload.notes = notes.trim();
    if (deliveredAt) {
      const d = new Date(deliveredAt);
      if (!isNaN(d)) {
        const iso = d.toISOString();
        const existingIso = dc?.time_delivered ? new Date(dc.time_delivered).toISOString() : '';
        if (iso !== existingIso) payload.time_delivered = iso;
      }
    }

    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save');
      setEditing(false);
      return;
    }

    setSavingText(true);
    await operation.run({
      label: 'Saving delivery details',
      execute: config => updateOrderDelivery(order.id, payload, { token: config.headers.Authorization.slice(7) }),
      accepted: async () => {
        toast.success('Delivery updated');
        setEditing(false);
        await onUpdated?.();
      },
      rejected: err => {
        if (/run is closed/i.test(String(err.response?.data?.detail))) { toast.error(podError(err)); return true; }
        return false;
      },
    });
    if (operation.owns()) setSavingText(false);
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !operation.canAct()) return;
    setAddingPhoto(true);
    await operation.run({
      label: 'Adding delivery photos',
      execute: config => addOrderDeliveryMedia(order.id, files, { token: config.headers.Authorization.slice(7) }),
      accepted: async () => {
        toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} saved to the delivery record`);
        await onUpdated?.();
      },
      errorMessage: 'Delivery photo update failed',
    });
    if (operation.owns()) {
      setAddingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  if (!hasConfirmation && order?.status !== 'delivered' && order?.status !== 'failed') {
    return null;
  }

  return (
    <div className="rounded-[10px] border border-black/[0.08] bg-[#fbfaf7] p-5 space-y-4" data-testid="delivery-confirmation-panel">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-black/40">Confirmation</p>
        {canEdit && !editing && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={startEdit} data-testid="edit-pod-btn">
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => photoInputRef.current?.click()}
              disabled={operation.blocked || addingPhoto}
              data-testid="add-delivery-photo-btn"
            >
              {addingPhoto ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" />Photo</>}
            </Button>
          </div>
        )}
      </div>

      <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />

      {editing ? (
        <div className="space-y-3" data-testid="pod-edit-form">
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Received by</Label>
            <Input value={rb} onChange={(e) => setRb(e.target.value)} data-testid="pod-received-by-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Relationship</Label>
            <Input value={rel} onChange={(e) => setRel(e.target.value)} data-testid="pod-relationship-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Delivered at</Label>
            <Input type="datetime-local" value={deliveredAt} onChange={(e) => setDeliveredAt(e.target.value)} data-testid="pod-time-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="pod-notes-input" />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={saveText} disabled={operation.blocked || savingText} data-testid="pod-save-btn">
              {savingText ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={savingText} data-testid="pod-cancel-btn">
              Cancel
            </Button>
          </div>
        </div>
      ) : !dc || !hasConfirmation ? (
        <p className="text-sm text-stone-400 italic">No confirmation data recorded</p>
      ) : (
        <>
          {/* Received by */}
          <div className="space-y-1 border-b border-stone-100 pb-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Received by</p>
            <p className="text-sm font-medium text-stone-900">
              {dc.received_by || <span className="text-stone-400 italic">Not recorded</span>}
              {dc.received_by_relationship && (
                <span className="text-stone-500 font-normal"> ({dc.received_by_relationship})</span>
              )}
            </p>
          </div>

          {/* Driver, or manual back-entry attribution */}
          {dc.manual_entry ? (
            <div className="space-y-1 border-b border-stone-100 pb-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Recorded by</p>
              <div className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-stone-400" />
                <span className="text-sm text-stone-700">Manually recorded by {dc.manually_recorded_by || 'admin'}</span>
              </div>
            </div>
          ) : dc.confirmed_by_user_name && (
            <div className="space-y-1 border-b border-stone-100 pb-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Driver</p>
              <div className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-stone-400" />
                <span className="text-sm text-stone-700">{dc.confirmed_by_user_name}</span>
              </div>
            </div>
          )}

          {/* Time */}
          {(dc.time_delivered || dc.confirmed_at) && (
            <div className="space-y-1 border-b border-stone-100 pb-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Time</p>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-stone-400" />
                <span className="text-sm text-stone-700">{fmtTime(dc.time_delivered || dc.confirmed_at)}</span>
              </div>
            </div>
          )}

          {/* GPS */}
          {dc.gps_location?.latitude != null && Number(dc.gps_location.latitude) !== 0 && (
            <div className="space-y-1 border-b border-stone-100 pb-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">GPS Location</p>
              <a
                href={`https://www.google.com/maps?q=${dc.gps_location.latitude},${dc.gps_location.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-emerald-700 hover:underline"
              >
                <MapPin className="h-3.5 w-3.5" />
                {Number(dc.gps_location.latitude).toFixed(5)}, {Number(dc.gps_location.longitude).toFixed(5)}
              </a>
              {dc.gps_location.accuracy && (
                <span className="text-[10px] text-stone-400 ml-5">({Math.round(Number(dc.gps_location.accuracy))}m accuracy)</span>
              )}
              <a
                href={`https://www.google.com/maps?q=${dc.gps_location.latitude},${dc.gps_location.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-2 rounded-lg overflow-hidden border border-stone-200 hover:border-stone-300 transition-colors"
                data-testid="delivery-static-map"
              >
                <img
                  src={`https://maps.googleapis.com/maps/api/staticmap?center=${dc.gps_location.latitude},${dc.gps_location.longitude}&zoom=15&size=400x250&scale=2&markers=color:red|${dc.gps_location.latitude},${dc.gps_location.longitude}&key=${process.env.REACT_APP_GOOGAPI_FRONT}`}
                  alt="Delivery location"
                  className="w-full h-40 object-cover"
                  loading="lazy"
                />
              </a>
            </div>
          )}

          {/* Notes — always shown; placeholder when empty (editable via the POD form). */}
          <div className="space-y-1 border-b border-stone-100 pb-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Notes</p>
            {dc.notes
              ? <p className="text-sm text-stone-700 italic">{dc.notes}</p>
              : <p className="text-sm text-stone-400 italic" data-testid="delivery-notes-empty">No notes recorded</p>}
          </div>

          {/* Signature — captured as a 'signature' media file (resolved to sigUrl
              above) or a legacy dc.signature_url. Read-only; placeholder when absent. */}
          <div className="space-y-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">Signature</p>
            {(sigUrl || dc.signature_url) ? (
              <div className="bg-white border border-stone-200 rounded-md p-3" data-testid="signature-display">
                <img src={sigUrl || dc.signature_url} alt="Signature" className="max-h-20 mx-auto" />
              </div>
            ) : signature ? (
              <p className="text-sm text-stone-500" data-testid="signature-unavailable">
                {offlineMediaLabel(signatureResult)}
              </p>
            ) : (
              <p className="text-sm text-stone-400 italic" data-testid="signature-empty">No signature captured</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ── Main Dialog ─────────────────────────────────────────── */
const OrderEditorOpening = ({ order: incomingOrder, onClose, onSaved }) => {
  // List refreshes are observations, not permission to reinitialize a draft.
  const [order] = useState(incomingOrder);
  const [formData, setFormDataState] = useState(EMPTY_FORM);
  const [commitmentStatus, setCommitmentStatus] = useState(null);
  const [membershipCheck, setMembershipCheck] = useState({ state: 'loading' });
  const [membershipRetry, setMembershipRetry] = useState(0);
  const [showStatusWarning, setShowStatusWarning] = useState(false);
  const [addressConfirmation, setAddressConfirmation] = useState(null);
  const [activeTab, setActiveTab] = useState('recipient');
  const [currentOrder, setCurrentOrder] = useState(order);
  const [reviewed, setReviewed] = useState(false);   // "Reviewed for accuracy" toggle
  const [deliveryDirty, setDeliveryDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [unknownReviewed, setUnknownReviewed] = useState(false);
  const initialDraft = useRef(null);
  const localOrder = order?.desktop_sync?.origin === 'local';
  const offlineCachedOrder = Boolean(order && isDesktopOffline() && !localOrder);
  const mode = `${incomingOrder?.desktop_sync?.origin === 'local'}:${isDesktopOffline()}`;
  const operation = useOrderEditorOperation(mode);
  const netPreview = useOrderNetAmounts([{ ...formData, id: order.id, desktop_sync: order.desktop_sync }],
    { draft: true, enabled: operation.visible && activeTab === 'details' });
  const setFormData = update => {
    if (!operation.canAct()) return;
    setFormDataState(update);
    setAddressConfirmation(null);
    setShowStatusWarning(false);
  };
  const dirty = initialDraft.current !== null
    && initialDraft.current !== JSON.stringify([formData, reviewed]);
  const localStatusEditable = localOrder && ['accepted', 'designed'].includes(formData.status);
  const membershipReady = membershipCheck.orderId === order?.id
    && membershipCheck.local === localOrder && membershipCheck.state === 'ready';
  const dateRestriction = membershipReady ? commitmentStatus?.delivery_date_restriction : null;
  const dateLocked = !membershipReady || commitmentStatus?.is_committed || dateRestriction?.locked;
  const dateLockMessage = !membershipReady
    ? (membershipCheck.state === 'error'
      ? 'Run assignment could not be checked. Retry before changing the delivery date.'
      : 'Checking run assignment before allowing date changes...')
    : dateRestriction?.message || (commitmentStatus?.is_committed ? 'Committed — delivery date locked.' : null);
  const activeSizes = useOrderSizes();
  // `order` is the captured opening: keep its historical choice available for undo.
  const sizes = orderSizeOptions(orderSizeOptions(activeSizes, order.size), formData.size);
  // SM-1: how this order's network_source resolves through the source-mapping
  // table (read-only audit readout below). null = still loading.
  const [resolvedSource, setResolvedSource] = useState(null);
  useEffect(() => {
    const config = operation.config();
    if (!order?.id || !config) return;
    let cancelled = false;
    operation.read(request => getOrderSourceResolution(order.id, request))
      .then(data => { if (!cancelled && operation.owns()) setResolvedSource(data); })
      .catch(() => { if (!cancelled && operation.owns()) setResolvedSource({ unavailable: true }); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // Parallel readout for the sending-florist string (subsource → florist mapping).
  const [resolvedSubsource, setResolvedSubsource] = useState(null);
  useEffect(() => {
    const config = operation.config();
    if (!order?.id || !config) return;
    let cancelled = false;
    operation.read(request => getOrderSubsourceResolution(order.id, request))
      .then(data => { if (!cancelled && operation.owns()) setResolvedSubsource(data); })
      .catch(() => { if (!cancelled && operation.owns()) setResolvedSubsource({ unavailable: true }); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // Resolve the order → catalog product for a read-only catalog match and its
  // reference-photo state. Catalog facts never populate or mutate order fields.
  const [resolvedProduct, setResolvedProduct] = useState(null);
  useEffect(() => {
    const config = operation.config();
    if (!order?.id || !config) return;
    let cancelled = false;
    operation.read(request => getOrderProductResolution(order.id, request))
      .then(data => { if (!cancelled && operation.owns()) setResolvedProduct(data); })
      .catch(() => { if (!cancelled && operation.owns()) setResolvedProduct({ unavailable: true }); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // Reference photo of the matched product: size-specific → default → none.
  const productReference = useProductReferenceMedia(resolvedProduct);

  const hasProofsData = !localOrder && !offlineCachedOrder;

  // Refresh order data after media upload
  const handleMediaAdded = useCallback(async () => {
    const config = operation.config();
    if (!config) return;
    const refreshedOrder = await operation.read(request => getOrder(order.id, request));
    if (operation.owns()) {
      if (refreshedOrder?.id !== order.id) throw new Error('Unexpected order response');
      setCurrentOrder(refreshedOrder);
    }
  // The opening is stable; operation methods check live ownership themselves.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  useEffect(() => {
    if (!order) return;
    const initial = {
      recipient_name: order.recipient_name || '',
      recipient_street_address: order.recipient_street_address || '',
      recipient_street_address_2: order.recipient_street_address_2 || '',
      recipient_city: order.recipient_city || '',
      recipient_state: order.recipient_state || '',
      recipient_zip_code: order.recipient_zip_code || '',
      recipient_phone: formatPhoneNumberDisplay(order.recipient_phone) || '',
      order_number: order.order_number || '',
      network_source: order.network_source || '',
      subsource_extracted: order.subsource_extracted || '',
      delivery_date: order.delivery_date || '',
      order_accepted_date: order.order_accepted_date || '',
      card_message: order.card_message || '',
      special_instructions: order.special_instructions || '',
      product_description: order.product_description || '',
      product_name: order.product_name || '',
      product_code: order.product_code || '',
      size: order.size || '',
      delivery_time_requirement: order.delivery_time_requirement || '',
      total_price: order.total_price || '',
      internal_order_number: order.internal_order_number || '',
      status: order.status || 'designed'
    };
    setFormDataState(initial);
    initialDraft.current = JSON.stringify([initial, !!order.reviewed_by]);
    setCommitmentStatus(null);
    setShowStatusWarning(false);
    setActiveTab('recipient');
    setCurrentOrder(order);
    setReviewed(!!order.reviewed_by);

  }, [order]);

  // Independent of form initialization: retrying must not discard entered values.
  useEffect(() => {
    const config = operation.config();
    if (!order?.id || offlineCachedOrder || !config) return undefined;
    let cancelled = false;
    const identity = { orderId: order.id, local: localOrder };
    setMembershipCheck({ ...identity, state: 'loading' });
    const check = async () => {
      try {
        const status = await operation.read(request => getOrderCommitmentStatus(order.id, { ...request, local: localOrder }));
        if (typeof status?.delivery_date_restriction?.locked !== 'boolean') {
          throw new Error('Delivery-date eligibility unavailable');
        }
        if (!cancelled && operation.owns()) {
          setCommitmentStatus(status);
          setMembershipCheck({ ...identity, state: 'ready' });
        }
      } catch (error) {
        if (!cancelled && operation.owns()) setMembershipCheck({ ...identity, state: 'error' });
      }
    };
    check();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, localOrder, offlineCachedOrder, membershipRetry]);

  const handleSave = async () => {
    if (!operation.canAct()) return;
    const snapshot = { fields: { ...formData }, reviewed };
    if (offlineCachedOrder) {
      if (!['recipient', 'admin'].includes(activeTab)) {
        toast.error('Only recipient and limited admin fields can be edited offline right now.');
        return;
      }
      const admin = activeTab === 'admin';
      const payload = admin ? {
            field_group: 'admin',
            status: snapshot.fields.status,
            order_accepted_date: snapshot.fields.order_accepted_date,
            order_number: snapshot.fields.order_number,
          } : Object.fromEntries(['recipient_name', 'recipient_phone', 'recipient_street_address',
            'recipient_street_address_2', 'recipient_city', 'recipient_state', 'recipient_zip_code']
            .map(key => [key, snapshot.fields[key]]));
      await operation.run({
        label: 'Saving offline edits',
        execute: config => createCachedOrderFieldMutation(order.id, {
          ...payload, ...(offlineEditContext(order, admin ? 'admin' : 'recipient')
            ? { edit_context: offlineEditContext(order, admin ? 'admin' : 'recipient') } : {}),
        }, config),
        accepted: () => finishSave(snapshot, `${admin ? 'Admin' : 'Recipient'} changes saved on this computer`, Object.keys(payload)),
        errorMessage: 'Failed to save offline order edit',
      });
      return;
    }
    const savedStatus = JSON.parse(initialDraft.current)[0].status;
    const wasInRun = savedStatus === 'on_route' || savedStatus === 'delivered';
    const stillInRun = formData.status === 'on_route' || formData.status === 'delivered';
    if (wasInRun && !stillInRun) {
      setShowStatusWarning(snapshot);
      return;
    }
    await persistUpdate(snapshot);
  };

  const finishSave = async (snapshot, message, savedKeys) => {
    toast.success(message);
    const baseline = JSON.parse(initialDraft.current);
    const fields = savedKeys
      ? { ...baseline[0], ...Object.fromEntries(savedKeys.filter(key => key in snapshot.fields).map(key => [key, snapshot.fields[key]])) }
      : snapshot.fields;
    initialDraft.current = JSON.stringify([fields, savedKeys ? baseline[1] : snapshot.reviewed]);
    setAddressConfirmation(null);
    setShowStatusWarning(false);
    await onSaved?.(order.id, { owns: operation.owns, config: operation.config() });
    if (!operation.owns()) return;
    if (deliveryDirty || initialDraft.current !== JSON.stringify([snapshot.fields, snapshot.reviewed])) {
      operation.report('Saved. Other unfinished edits remain in this editor.');
      return;
    }
    operation.retire();
    onClose();
  };

  // Separated so accepting Google's address can retry the same save with an
  // explicit acceptance. See TCA-26092105.
  const persistUpdate = async (snapshot, { review = null } = {}) => {
    if (!operation.canAct()) return;
    const acceptance = review ? addressAcceptance(review) : null;
    if (review && !acceptance) return;
    if (snapshot.fields.delivery_date !== (order.delivery_date || '') && dateLocked) {
      toast.error(dateLockMessage || 'Remove the order from its run before changing the delivery date.');
      return;
    }
    // The captured snapshot, not a still-open prompt, owns the pending write.
    // Unknown/refresh-only recovery must remain reachable in the main editor.
    setAddressConfirmation(null);
    setShowStatusWarning(false);
    await operation.run({
      label: 'Saving order',
      execute: config => localOrder ? updateLocalOrder(order.id, snapshot.fields, config)
        : updateOrder(order.id, {
          ...snapshot.fields,
          accuracy_reviewed: snapshot.reviewed,
          ...acceptance,
        }, config),
      accepted: result => {
        let acknowledged = snapshot;
        if (acceptance && result?.id === order.id) {
          const canonical = Object.fromEntries(['recipient_street_address', 'recipient_city', 'recipient_state', 'recipient_zip_code']
            .filter(key => typeof result[key] === 'string').map(key => [key, result[key]]));
          acknowledged = { ...snapshot, fields: { ...snapshot.fields, ...canonical } };
          setFormDataState(acknowledged.fields);
        }
        return finishSave(acknowledged, localOrder ? 'Unsynced order updated on this computer' : 'Order updated successfully');
      },
      rejected: error => {
        const addressReview = addressReviewFrom(error);
        if (!addressReview) return false;
        setAddressConfirmation({ ...addressReview, snapshot });
        return true;
      },
      errorMessage: 'Failed to update order',
    });
  };

  const handleCancel = () => {
    if (dirty || deliveryDirty || operation.isPending() || operation.unknown) {
      setConfirmClose(true);
      return;
    }
    operation.retire();
    onClose();
  };

  // Per-field low-confidence markers. The full (unexcluded) set the AI was
  // unsure about, attached live by the backend. Suppressed once a human has
  // marked the order Reviewed (checking the box hides them immediately).
  const flaggedSet = React.useMemo(
    () => new Set(reviewed ? [] : (order?.low_confidence_fields_all || [])),
    [reviewed, order]
  );
  const FieldFlag = ({ field }) =>
    flaggedSet.has(field) ? (
      <span
        className="ml-1 inline-flex -translate-y-px items-center text-amber-500"
        title="The AI wasn't fully sure about this field — double-check it against the original order."
        data-testid={`edit-field-flag-${field}`}
      >
        <AlertTriangle className="h-3 w-3" strokeWidth={2.5} />
      </span>
    ) : null;
  // Which fields live on which tab — drives the ⚠ dot on a tab whose contents
  // include a flagged field (so the user knows where to look).
  const TAB_FIELDS = {
    recipient: ['recipient_name', 'recipient_phone', 'recipient_street_address', 'recipient_street_address_2', 'recipient_city', 'recipient_state', 'recipient_zip_code'],
    details: ['product_description', 'product_name', 'product_code', 'total_price', 'delivery_date', 'delivery_time_requirement', 'card_message', 'special_instructions'],
    admin: ['order_number', 'network_source', 'subsource_extracted'],
  };
  const tabHasFlag = (tab) => (TAB_FIELDS[tab] || []).some((f) => flaggedSet.has(f));
  const TabFlagDot = ({ tab }) =>
    tabHasFlag(tab) ? (
      <span
        className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle"
        data-testid={`tab-flag-dot-${tab}`}
        title="This tab has a field the AI was unsure about"
      />
    ) : null;

  if (!operation.visible) return null;

  return (
    <>
      <Dialog open={!!order} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent
          data-testid="edit-order-dialog"
          onOpenAutoFocus={(e) => {
            // Keep Radix from focusing the first control (the Recipient tab),
            // but move focus onto the dialog itself. Focus must land inside the
            // portal or the browser refuses the aria-hidden Radix applies to the
            // page behind it, leaving the background exposed to screen readers.
            // The focus-scope container already carries tabIndex -1.
            e.preventDefault();
            e.currentTarget.focus();
          }}
          className="max-w-5xl bg-[#fdfcfa]"
        >
          <DialogHeader>
            <div className="flex items-baseline justify-between gap-4">
              <DialogTitle className="text-[20px] font-semibold leading-tight text-[#1a1a1a]">
                {order?.recipient_name || 'Edit Order'}
              </DialogTitle>
              <DialogDescription className="shrink-0 text-right font-mono text-[12px] leading-normal text-black/40">
                {order?.internal_order_number}
                {(resolvedSource?.resolved ? resolvedSource.source_name : order?.network_source) && (
                  <>
                    <br />
                    {resolvedSource?.resolved ? resolvedSource.source_name : order?.network_source}
                    {order?.order_number ? ` #${order.order_number}` : ''}
                  </>
                )}
              </DialogDescription>
            </div>
            {/* Reviewed-for-accuracy — opt-in human stamp; server records who/when. */}
            {offlineCachedOrder && (
              <div
                className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
                data-testid="offline-cached-readonly-notice"
              >
              Recipient fields and limited Admin-tab fields can be edited offline on this computer. Other fields remain read-only until a later offline-edit release.
              </div>
            )}
            {!localOrder && !offlineCachedOrder && (
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <Checkbox
                id="edit-reviewed-toggle"
                checked={reviewed}
                disabled={operation.blocked}
                onCheckedChange={(v) => {
                  if (!operation.canAct()) return;
                  setReviewed(v === true);
                  setAddressConfirmation(null);
                  setShowStatusWarning(false);
                }}
                data-testid="edit-reviewed-checkbox"
              />
              <label htmlFor="edit-reviewed-toggle" className="text-[13px] font-medium text-stone-600 cursor-pointer">Reviewed</label>
              {reviewed && order?.reviewed_by && (
                <span className="inline-flex items-center gap-1 text-[11px] italic text-emerald-600" data-testid="edit-reviewed-stamp">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Reviewed by {order.reviewed_by}{order.reviewed_at ? ` on ${new Date(order.reviewed_at).toLocaleString()}` : ''}
                </span>
              )}
              {reviewed && !order?.reviewed_by && (
                <span className="text-[11px] italic text-stone-400">recorded on save</span>
              )}
            </div>
            )}
          </DialogHeader>

          {operation.pending && <p role="status" data-testid="editor-pending">{operation.pending}…</p>}
          {operation.notice && <p role="status" data-testid="editor-notice">{operation.notice}</p>}
          {operation.refreshFailed && <Button disabled={!!operation.pending} onClick={operation.retryRefresh} data-testid="editor-retry-refresh">Retry refresh only</Button>}
          {operation.unknown && (
            <div role="alert" className="space-y-2 rounded border border-amber-300 p-3" data-testid="editor-unknown">
              <p>{operation.unknown}</p>
              <p className="text-sm">Inspect this order in another view or tab first. A matching display does not prove every effect. Do not repeat a photo upload or offline proposal that is already present.</p>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={unknownReviewed} onChange={event => setUnknownReviewed(event.target.checked)} data-testid="editor-reviewed-unknown" />
                I checked the saved record and understand a repeated action may duplicate work.
              </label>
              <Button disabled={!unknownReviewed} onClick={() => { operation.reviewedUnknown(); setUnknownReviewed(false); }} data-testid="editor-allow-retry">Allow another attempt</Button>
            </div>
          )}
          <fieldset disabled={operation.blocked} className="min-w-0" data-testid="editor-fields">
          <Tabs value={activeTab} onValueChange={value => { if (operation.canAct()) setActiveTab(value); }} className="w-full">
            <TabsList className="w-full gap-1 rounded-[9px] bg-black/[0.035] p-1 mb-4">
              <TabsTrigger
                value="recipient"
                className="flex-1 rounded-[6px] text-[12.5px] font-medium text-black/50 data-[state=active]:bg-white data-[state=active]:text-[#1a1a1a] data-[state=active]:shadow-sm"
                data-testid="recipient-tab"
              >
                Recipient<TabFlagDot tab="recipient" />
              </TabsTrigger>
              <TabsTrigger
                value="details"
                className="flex-1 rounded-[6px] text-[12.5px] font-medium text-black/50 data-[state=active]:bg-white data-[state=active]:text-[#1a1a1a] data-[state=active]:shadow-sm"
                data-testid="order-details-tab"
              >
                Order<TabFlagDot tab="details" />
              </TabsTrigger>
              <TabsTrigger
                value="admin"
                className="flex-1 rounded-[6px] text-[12.5px] font-medium text-black/50 data-[state=active]:bg-white data-[state=active]:text-[#1a1a1a] data-[state=active]:shadow-sm"
                data-testid="admin-tab"
              >
                Admin<TabFlagDot tab="admin" />
              </TabsTrigger>
              {hasProofsData && (
                <TabsTrigger
                  value="proofs"
                  className="flex-1 rounded-[6px] text-[12.5px] font-medium text-black/50 data-[state=active]:bg-white data-[state=active]:text-[#1a1a1a] data-[state=active]:shadow-sm"
                  data-testid="media-gallery-tab"
                >
                  Fulfillment
                </TabsTrigger>
              )}
            </TabsList>

            {/* ── Recipient Tab ── */}
            <TabsContent value="recipient" className="mt-0">
              <div className="max-h-[60vh] overflow-y-auto pr-1 pt-1">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                  {/* Recipient panel */}
                  <div className={`${CD.panel} sm:flex-1`}>
                    <div className={CD.panelTitle}>Recipient</div>
                    <div className="flex flex-col gap-3">
                      <div>
                        <Label className={CD.label}>Name<FieldFlag field="recipient_name" /></Label>
                        <Input value={formData.recipient_name} onChange={(e) => setFormData({...formData, recipient_name: e.target.value})} className={CD.input} data-testid="edit-input-name" />
                      </div>
                      <div>
                        <Label className={CD.label}>Phone<FieldFlag field="recipient_phone" /></Label>
                        <Input value={formData.recipient_phone} onChange={(e) => setFormData({...formData, recipient_phone: e.target.value})} className={CD.input} data-testid="edit-input-phone" />
                      </div>
                    </div>
                  </div>

                  {/* Address panel */}
                  <div className={`${CD.panel} sm:flex-[1.6]`}>
                    <div className={CD.panelTitle}>Address</div>
                    <div className="flex flex-col gap-4 sm:flex-row">
                      <div className="flex flex-1 flex-col gap-3">
                        <div>
                          <Label className={CD.label}>Street Address<FieldFlag field="recipient_street_address" /></Label>
                          <AddressAutocomplete
                            value={formData.recipient_street_address}
                            onChange={(value) => setFormData(prev => ({...prev, recipient_street_address: value}))}
                            onAddressSelect={(address) => {
                              setFormData(prev => ({
                                ...prev,
                                recipient_street_address: address.street,
                                recipient_city: address.city || prev.recipient_city,
                                recipient_state: address.state || prev.recipient_state,
                                recipient_zip_code: address.zip || prev.recipient_zip_code,
                              }));
                            }}
                            placeholder="Start typing an address..."
                            className={CD.input}
                          />
                        </div>
                        <div>
                          <Label className={CD.label}>Apt, Suite, Unit<FieldFlag field="recipient_street_address_2" /></Label>
                          <Input value={formData.recipient_street_address_2} onChange={(e) => setFormData({...formData, recipient_street_address_2: e.target.value})} className={CD.input} data-testid="edit-input-street-2" />
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          <div className="col-span-2">
                            <Label className={CD.label}>City<FieldFlag field="recipient_city" /></Label>
                            <Input value={formData.recipient_city} onChange={(e) => setFormData({...formData, recipient_city: e.target.value})} className={CD.input} data-testid="edit-input-city" />
                          </div>
                          <div>
                            <Label className={CD.label}>State<FieldFlag field="recipient_state" /></Label>
                            <Input value={formData.recipient_state} onChange={(e) => setFormData({...formData, recipient_state: e.target.value})} maxLength={2} className={CD.input} data-testid="edit-input-state" />
                          </div>
                          <div>
                            <Label className={CD.label}>Zip Code<FieldFlag field="recipient_zip_code" /></Label>
                            <Input value={formData.recipient_zip_code} onChange={(e) => setFormData({...formData, recipient_zip_code: e.target.value})} className={CD.input} data-testid="edit-input-zip" />
                          </div>
                        </div>
                      </div>
                      <div className="flex h-[150px] w-full flex-none items-center justify-center rounded-[9px] p-1 text-center font-mono text-[10px] text-black/40 sm:w-[150px]" style={CD_STRIPE} title="Map — real static map/Street View arrives later">
                        map placeholder
                      </div>
                    </div>
                    {/* Access Info — read-only (edits live in Settings → Access Codes). */}
                    <div className="mt-3.5">
                      <Label className={CD.label}>Access Info</Label>
                      <SafeRender>
                        <AddressIntel orderId={order?.id} addressKey={savedAccessAddressKey(currentOrder)} variant="pill" />
                      </SafeRender>
                      <p className="text-[11px] text-stone-500">Access information is for the saved order address.</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ── Order Details Tab ── */}
            <fieldset disabled={offlineCachedOrder} className={offlineCachedOrder ? 'opacity-70' : undefined}>
            <TabsContent value="details" className="mt-0">
              <div className="max-h-[60vh] overflow-y-auto pr-1 pt-1">
                {/* Stat strip — read-only summaries of the fields edited below */}
                <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="order-summary-strip">
                  <div className={CD.stat}>
                    <div className={CD.statK}>Delivery</div>
                    <div className={CD.statV}>
                      {formData.delivery_date || formData.delivery_time_requirement
                        ? `${formData.delivery_date ? formatDeliveryDateDisplay(formData.delivery_date) : '—'} · ${(formData.delivery_time_requirement || '').trim() || 'any time'}`
                        : '—'}
                    </div>
                  </div>
                  <div className={CD.stat}>
                    <div className={CD.statK}>Code</div>
                    <div className={CD.statV}>{formData.product_code || '—'}</div>
                  </div>
                  <div className={CD.stat}>
                    <div className={CD.statK}>Total</div>
                    <div className={CD.statV}>{formData.total_price ? `$${formData.total_price}` : '—'}</div>
                  </div>
                  <div className={`${CD.stat} min-w-0`}>
                    <div className={`${CD.statK} flex items-center gap-1`}>NET
                      <TooltipProvider delayDuration={150}><Tooltip><TooltipTrigger asChild>
                        <span tabIndex={0} role="img" aria-label={NET_EXPLANATION}
                          className="inline-flex text-stone-400"><Info className="h-3 w-3" strokeWidth={2.5} /></span>
                      </TooltipTrigger><TooltipContent className="max-w-xs">{NET_EXPLANATION}</TooltipContent></Tooltip></TooltipProvider>
                    </div>
                    <div className={`${CD.statV} text-right`}><OrderNetValue
                      result={netPreview.forOrder(order)} onRetry={netPreview.retry} /></div>
                  </div>
                </div>

                {/* Two panels: Product (wider) + Message */}
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                  {/* Product panel */}
                  <div className={`${CD.panel} sm:flex-[1.5]`}>
                    <div className={CD.panelTitle}>Product</div>
                    <div className="mb-3.5 flex items-stretch gap-4">
                      {productReference.imageUrl ? (
                        <div className="flex w-24 flex-none items-center justify-center overflow-hidden rounded-[9px] border border-black/10 bg-white" title="Product reference photo (from the catalog)">
                          <img src={productReference.imageUrl} alt="Product reference" className="h-full w-full object-cover" />
                        </div>
                      ) : (
                        <div
                          className="flex w-24 flex-none items-center justify-center rounded-[9px] p-1 text-center font-mono text-[10px] text-black/40"
                          style={CD_STRIPE}
                          title={productReferenceMediaLabel(
                            productReference,
                            'No catalog photo for this product yet',
                          )}
                          data-testid={productReference.configured
                            ? 'edit-reference-unavailable'
                            : 'edit-reference-empty'}
                        >
                          {productReferenceMediaLabel(productReference, 'product photo')}
                        </div>
                      )}
                      <div className="flex-1">
                        <Label className={CD.label}>Description<FieldFlag field="product_description" /></Label>
                        <Textarea value={formData.product_description} onChange={(e) => setFormData({...formData, product_description: e.target.value})} rows={3} className={`${CD.input} resize-none`} data-testid="edit-input-product-description" />
                      </div>
                    </div>
                    <CatalogMatch resolution={resolvedProduct} />
                    <div className="grid grid-cols-3 gap-3.5">
                      <div>
                        <Label className={CD.label}>Size</Label>
                        <Select
                          disabled={!activeSizes.length}
                          value={formData.size || '__none__'}
                          onValueChange={(value) => setFormData({...formData, size: value === '__none__' ? '' : value})}
                        >
                          <SelectTrigger className={CD.input} data-testid="edit-input-size-select"><SelectValue placeholder="None" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {!activeSizes.length && <SizeChoicesUnavailable />}
                      </div>
                      <div>
                        <Label className={CD.label}>Code<FieldFlag field="product_code" /></Label>
                        <Input value={formData.product_code} onChange={(e) => setFormData({...formData, product_code: e.target.value})} disabled={localOrder} title={localOrder ? 'Available after synchronization' : undefined} className={`${CD.input} font-mono text-[12.5px] md:text-[12.5px]`} data-testid="edit-input-product-code" />
                      </div>
                      <div>
                        <Label className={CD.label}>Product Name<FieldFlag field="product_name" /></Label>
                        <Input value={formData.product_name} onChange={(e) => setFormData({...formData, product_name: e.target.value})} disabled={localOrder} title={localOrder ? 'Available after synchronization' : undefined} className={CD.input} data-testid="edit-input-product-name" />
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-3.5">
                      <div>
                        <Label className={CD.label}>Date<FieldFlag field="delivery_date" /></Label>
                        {dateLocked && (
                          <div className="mb-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-600" data-testid="delivery-date-restriction">
                            {dateLockMessage}
                            {membershipCheck.state === 'error' && (
                              <button type="button" className="ml-1 underline" onClick={() => setMembershipRetry(value => value + 1)}>Retry run check</button>
                            )}
                          </div>
                        )}
                        <div className="block sm:hidden">
                          <Input type="date" value={formData.delivery_date || ''} onChange={(e) => !dateLocked && setFormData({...formData, delivery_date: e.target.value})} disabled={dateLocked} className={CD.input} data-testid="edit-input-delivery-date-mobile" />
                        </div>
                        <div className="hidden sm:block">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className={`w-full justify-start font-normal ${CD.input} ${dateLocked ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={dateLocked} data-testid="edit-input-delivery-date">
                                <CalendarIcon className="mr-2 h-4 w-4 text-black/40" />
                                {formData.delivery_date ? formatDeliveryDateDisplay(formData.delivery_date) : <span className="text-black/40">Select date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar mode="single" selected={formData.delivery_date ? parseDeliveryDate(formData.delivery_date) : undefined} onSelect={(d) => d && !dateLocked && setFormData({...formData, delivery_date: formatDateForBackend(d)})} initialFocus />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      <div>
                        <Label className={CD.label}>Time<FieldFlag field="delivery_time_requirement" /></Label>
                        <Input value={formData.delivery_time_requirement} onChange={(e) => setFormData({...formData, delivery_time_requirement: e.target.value})} className={CD.input} data-testid="edit-input-delivery-time-requirement" />
                      </div>
                      <div>
                        <Label className={CD.label}>Total Price<FieldFlag field="total_price" /></Label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-black/40">$</span>
                          <Input type="number" step="0.01" min="0" value={formData.total_price} onChange={(e) => setFormData({...formData, total_price: e.target.value})} className={`${CD.input} pl-5`} data-testid="edit-input-total-price" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Message panel */}
                  <div className={`${CD.panel} sm:flex-1`}>
                    <div className={CD.panelTitle}>Message</div>
                    <div className="mb-3 rounded-r-[9px] border border-l-[3px] border-black/[0.08] border-l-[#1f4d2e] bg-white px-3.5 py-3">
                      <Label className="mb-1.5 block text-[11.5px] font-semibold tracking-[0.01em] text-[#1f4d2e]">Card Message<FieldFlag field="card_message" /></Label>
                      <Textarea value={formData.card_message} onChange={(e) => setFormData({...formData, card_message: e.target.value})} rows={6} className="resize-none border-0 bg-transparent p-0 text-[13.5px] shadow-none focus-visible:ring-0 md:text-[13.5px]" data-testid="edit-input-card-message" />
                    </div>
                    <Label className={CD.label}>Special Instructions<FieldFlag field="special_instructions" /></Label>
                    <Textarea value={formData.special_instructions} onChange={(e) => setFormData({...formData, special_instructions: e.target.value})} rows={2} className={`${CD.input} resize-none`} data-testid="edit-input-special-instructions" />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ── Admin Tab ── */}
            </fieldset>
            <fieldset disabled={false} className={offlineCachedOrder ? 'opacity-95' : undefined}>
            <TabsContent value="admin" className="mt-0">
              <div className="max-h-[60vh] overflow-y-auto pr-1 pt-1">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                  {/* Status & Numbers panel */}
                  <div className={`${CD.panel} sm:flex-1`}>
                    <div className={CD.panelTitle}>Status &amp; Numbers</div>
                    <div className="grid grid-cols-2 gap-3.5">
                      <div>
                        <Label className={CD.label}>Status</Label>
                        <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})} disabled={localOrder && !localStatusEditable}>
                          <SelectTrigger className={CD.input} data-testid="edit-input-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {!localOrder && <SelectItem value="new" disabled={offlineCachedOrder}>New (Quarantine)</SelectItem>}
                            <SelectItem value="accepted">Accepted</SelectItem>
                            <SelectItem value="designed">Designed</SelectItem>
                            {!localOrder && <SelectItem value="delivered" disabled={offlineCachedOrder}>Delivered</SelectItem>}
                            {!localOrder && <SelectItem value="failed" disabled={offlineCachedOrder}>Failed</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className={CD.label}>Accepted Date</Label>
                        <div className="block sm:hidden">
                          <Input type="date" value={formData.order_accepted_date || ''} onChange={(e) => setFormData({...formData, order_accepted_date: e.target.value})} className={CD.input} data-testid="edit-input-order-accepted-date-mobile" />
                        </div>
                        <div className="hidden sm:block">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className={`w-full justify-start font-normal ${CD.input}`} data-testid="edit-input-order-accepted-date">
                                <CalendarIcon className="mr-2 h-4 w-4 text-black/40" />
                                {formData.order_accepted_date ? formatDeliveryDateDisplay(formData.order_accepted_date) : <span className="text-black/40">Select date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar mode="single" selected={formData.order_accepted_date ? parseDeliveryDate(formData.order_accepted_date) : undefined} onSelect={(d) => d && setFormData({...formData, order_accepted_date: formatDateForBackend(d)})} initialFocus />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>
                    {commitmentStatus?.is_committed && (
                      <div className="mt-3.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-600">
                        Committed to "{commitmentStatus.run_name}" — internal number can't change.
                      </div>
                    )}
                    <div className="mt-3.5 grid grid-cols-2 gap-3.5">
                      <div>
                        <Label className={CD.label}>External Order #<FieldFlag field="order_number" /></Label>
                        <Input value={formData.order_number} onChange={(e) => setFormData({...formData, order_number: e.target.value})} className={CD.input} data-testid="edit-input-order-number" />
                      </div>
                      <div>
                        <Label className={CD.label}>Internal Order #</Label>
                        <Input value={formData.internal_order_number} onChange={(e) => setFormData({...formData, internal_order_number: e.target.value})} disabled={localOrder || offlineCachedOrder || commitmentStatus?.is_committed} title={localOrder ? 'Assigned after synchronization' : undefined} className={`${CD.input} ${(localOrder || offlineCachedOrder || commitmentStatus?.is_committed) ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="edit-input-internal-order-number" />
                      </div>
                    </div>
                  </div>

                  {/* Sources panel */}
                  <div className={`${CD.panel} sm:flex-1`}>
                    <div className={CD.panelTitle}>Sources</div>
                    <div className="flex flex-col gap-3.5">
                      <div>
                        <Label className={CD.label}>Network<FieldFlag field="network_source" /></Label>
                        <Input value={formData.network_source} onChange={(e) => setFormData({...formData, network_source: e.target.value})} placeholder="Raw source string" disabled={offlineCachedOrder} className={CD.input} data-testid="edit-input-network-source" />
                        {/* SM-1 read-only diagnostic: what network_source resolves to via the mapping. */}
                        <div className="mt-1.5 rounded-r-[8px] border-l-[3px] border-l-black/[0.15] bg-[#f7f6f3] px-3 py-2 text-[12px] leading-[1.5] text-black/55" data-testid="edit-source-resolved-readout">
                          <div>Raw source string: <span className="break-all font-mono font-semibold text-[#1a1a1a]">{order?.network_source || '—'}</span></div>
                          <div>
                            Resolves to (via mapping): {resolvedSource == null ? (
                              <span className="text-black/40">…</span>
                            ) : resolvedSource.resolved ? (
                              <span className="font-semibold text-[#1a1a1a]">{resolvedSource.source_name}</span>
                            ) : (
                              <span className="font-semibold text-[#8a6d1f]">{resolvedSource.unavailable ? 'Source lookup unavailable' : 'Unknown Source'}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label className={CD.label}>Florist<FieldFlag field="subsource_extracted" /></Label>
                        <Input value={formData.subsource_extracted} onChange={(e) => setFormData({...formData, subsource_extracted: e.target.value})} placeholder="Raw sending-florist string" disabled={localOrder || offlineCachedOrder} title={localOrder ? 'Available after synchronization' : undefined} className={CD.input} data-testid="edit-input-subsource" />
                        {/* Read-only diagnostic: what subsource_extracted resolves to via the florist mapping. */}
                        <div className="mt-1.5 rounded-r-[8px] border-l-[3px] border-l-black/[0.15] bg-[#f7f6f3] px-3 py-2 text-[12px] leading-[1.5] text-black/55" data-testid="edit-subsource-resolved-readout">
                          <div>Raw florist string: <span className="break-all font-mono font-semibold text-[#1a1a1a]">{order?.subsource_extracted || '—'}</span></div>
                          <div>
                            Resolves to (via mapping): {resolvedSubsource == null ? (
                              <span className="text-black/40">…</span>
                            ) : resolvedSubsource.resolved ? (
                              <span className="font-semibold text-[#1a1a1a]">{resolvedSubsource.sending_florist?.name}</span>
                            ) : (
                              <span className="font-semibold text-[#8a6d1f]">{resolvedSubsource.unavailable ? 'Florist lookup unavailable' : 'Unknown Florist'}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ── Fulfillment & Proofs Tab ── */}
            </fieldset>
            {hasProofsData && (
              <TabsContent value="proofs" forceMount className="mt-0 data-[state=inactive]:hidden">
                <SafeRender>
                <div className="max-h-[60vh] overflow-y-auto pr-1 pt-1">
                  {/* Stat strip — at-a-glance delivery facts */}
                  <div className="mb-5 flex gap-2.5">
                    <div className={CD.stat}>
                      <div className={CD.statK}>Driver</div>
                      <div className={CD.statV}>{currentOrder?.delivery_confirmation?.confirmed_by_user_name || '—'}</div>
                    </div>
                    <div className={CD.stat}>
                      <div className={CD.statK}>Received By</div>
                      <div className={CD.statV}>{currentOrder?.delivery_confirmation?.received_by || '—'}</div>
                    </div>
                    <div className={CD.stat}>
                      <div className={CD.statK}>Delivered</div>
                      <div className={CD.statV}>
                        {(currentOrder?.delivery_confirmation?.time_delivered || currentOrder?.delivery_confirmation?.confirmed_at)
                          ? fmtTime(currentOrder.delivery_confirmation.time_delivered || currentOrder.delivery_confirmation.confirmed_at)
                          : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Two panels: Photos (wider) + Confirmation */}
                  <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                    <div className={`${CD.panel} sm:flex-[1.6]`}>
                      <MediaGallery order={currentOrder} onMediaAdded={handleMediaAdded} operation={operation} />
                    </div>
                    <div className="sm:flex-1">
                      <DeliveryPanel order={currentOrder} onUpdated={handleMediaAdded} operation={operation} onDirtyChange={setDeliveryDirty} />
                    </div>
                  </div>
                </div>
                </SafeRender>
              </TabsContent>
            )}
          </Tabs>
          </fieldset>

          {activeTab !== 'proofs' && (
            <DialogFooter className="mt-4 border-t border-black/[0.07] pt-4">
              <Button variant="outline" onClick={handleCancel} data-testid="edit-cancel-button">Cancel</Button>
              {(!offlineCachedOrder || ['recipient', 'admin'].includes(activeTab)) && (
                <Button disabled={operation.blocked} onClick={handleSave} className="bg-[#1f4d2e] text-white hover:bg-[#173c23]" data-testid="edit-save-button">Save Changes</Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Status change warning dialog */}
      {addressConfirmation && (
        <AddressConfirmationDialog
          proposal={addressConfirmation.proposal}
          reason={addressConfirmation.reason}
          difference={addressConfirmation.difference}
          busy={operation.blocked}
          onAccept={() => persistUpdate(addressConfirmation.snapshot, { review: addressConfirmation })}
          onEdit={() => { if (operation.canAct()) setAddressConfirmation(null); }}
        />
      )}

      <Dialog open={!!showStatusWarning} onOpenChange={(open) => { if (!open && operation.canAct()) setShowStatusWarning(false); }}>
        <DialogContent data-testid="status-warning-dialog">
          <DialogHeader>
            <DialogTitle>Remove from delivery run?</DialogTitle>
            <DialogDescription>
              This order is currently on a route. Changing its status to "{showStatusWarning?.fields?.status}" will remove it from its assigned delivery run.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button disabled={operation.blocked} variant="outline" onClick={() => setShowStatusWarning(false)} data-testid="status-warning-cancel">Cancel</Button>
            <Button disabled={operation.blocked} variant="destructive" onClick={() => persistUpdate(showStatusWarning)} data-testid="status-warning-confirm">Change Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
        <DialogContent data-testid="editor-close-confirmation">
          <DialogHeader>
            <DialogTitle>Leave this editor?</DialogTitle>
            <DialogDescription>
              Unfinished edits will be discarded. Saved photos and other acknowledged changes are not undone.
              {(operation.pending || operation.unknown) && ' A request already sent may still save; leaving does not cancel it. Check the saved record before repeating it.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setConfirmClose(false)} data-testid="editor-stay">Keep editing</Button>
            <Button variant="destructive" onClick={() => { operation.retire(); onClose(); }} data-testid="editor-leave">Leave editor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// A close/reopen or order replacement creates a genuinely new opening, even
// when the parent keeps this outer dialog component mounted.
export const EditOrderDialog = props => props.order
  ? <OrderEditorOpening key={props.order.id} {...props} /> : null;

export default EditOrderDialog;
